import { useCallback } from 'react';
import { useDataProvider, useGetIdentity } from 'react-admin';

export type RateImportSchema = {
    carrier: string;
    cropyear: string;
    state: string;
    countycode: string;
    county: string;
    township: string;
    range: string;
    form: string;
    crop: string;
    a: string;
    b: string;
    c: string;
    d: string;
    e: string;
    f: string;
    g: string;
    h: string;
    i: string;
    j: string;
    k: string;
    l?: string;
    m?: string;
    n?: string;
    o: string;
    p?: string;
    p1: string;
    p2: string;
    a1?: string;
    a2?: string;
    r1?: string;
    r2?: string;
    e2?: string;
};

export function useRatesImport(selectedSpreadsheet: string) {
    const user = useGetIdentity();
    const dataProvider = useDataProvider();

    const processBatch = useCallback(
        async (batch: RateImportSchema[]) => {
            const userId = user?.identity?.id;

            // Validation for sales.id (number)
            if (
                userId === undefined ||
                userId === null ||
                typeof userId !== 'number' ||
                userId <= 0
            ) {
                console.error(
                    'useRatesImport: Invalid or missing user ID (sales.id). Value:',
                    userId
                );
                throw new Error(
                    'User identity (sales.id) not available for record creation.'
                );
            }

            // Process records sequentially within the batch
            let processedCount = 0;
            for (const rate of batch) {
                try {
                    // Use upsert to handle duplicate records gracefully
                    // First try to create, if it fails due to duplicate, try to update
                    try {
                        // Check if rate object has an id field (which it shouldn't)
                        if ('id' in rate) {
                            console.warn(
                                `useRatesImport: Rate object contains id field:`,
                                rate.id
                            );
                        }

                        const dataToInsert = {
                            carrier: rate.carrier,
                            crop_year: rate.cropyear,
                            state: rate.state,
                            county_code: rate.countycode,
                            county: rate.county,
                            township: rate.township,
                            range: rate.range,
                            form: rate.form,
                            crop: rate.crop,
                            a_rate: rate.a,
                            b_rate: rate.b,
                            c_rate: rate.c,
                            d_rate: rate.d,
                            e_rate: rate.e,
                            f_rate: rate.f,
                            g_rate: rate.g,
                            h_rate: rate.h,
                            i_rate: rate.i,
                            j_rate: rate.j,
                            k_rate: rate.k,
                            l_rate: rate.l,
                            m_rate: rate.m,
                            n_rate: rate.n,
                            o_rate: rate.o,
                            p_rate: rate.p,
                            p1_rate: rate.p1,
                            p2_rate: rate.p2,
                            a1_rate: rate.a1,
                            a2_rate: rate.a2,
                            r1_rate: rate.r1,
                            r2_rate: rate.r2,
                            e2_rate: rate.e2,
                            created_by: userId,
                            created_at: new Date().toISOString(),
                            spreadsheet_type: selectedSpreadsheet,
                        };

                        // Ensure no id field is included
                        if ('id' in dataToInsert) {
                            delete (dataToInsert as any).id;
                        }

                        await dataProvider.create('rates', {
                            data: dataToInsert,
                        });
                    } catch (createError: any) {
                        // If create fails due to duplicate key constraint, try to find and update the existing record
                        if (
                            createError?.message?.includes(
                                'duplicate key value violates unique constraint'
                            )
                        ) {
                            console.log(
                                `useRatesImport: Record exists, attempting to update: ${rate.carrier} ${rate.state} ${rate.county} ${rate.township} ${rate.range} ${rate.form}`
                            );

                            // Find the existing record to get its ID
                            const existingRecords = await dataProvider.getList(
                                'rates',
                                {
                                    filter: {
                                        carrier: rate.carrier,
                                        crop_year: rate.cropyear,
                                        state: rate.state,
                                        county_code: rate.countycode,
                                        county: rate.county,
                                        township: rate.township,
                                        range: rate.range,
                                        form: rate.form,
                                        crop: rate.crop,
                                        spreadsheet_type: selectedSpreadsheet,
                                    },
                                    pagination: { page: 1, perPage: 1 },
                                    sort: { field: 'id', order: 'ASC' },
                                }
                            );

                            if (existingRecords.data.length > 0) {
                                // Update the existing record
                                await dataProvider.update('rates', {
                                    id: existingRecords.data[0].id,
                                    data: {
                                        carrier: rate.carrier,
                                        crop_year: rate.cropyear,
                                        state: rate.state,
                                        county_code: rate.countycode,
                                        county: rate.county,
                                        township: rate.township,
                                        range: rate.range,
                                        form: rate.form,
                                        crop: rate.crop,
                                        a_rate: rate.a,
                                        b_rate: rate.b,
                                        c_rate: rate.c,
                                        d_rate: rate.d,
                                        e_rate: rate.e,
                                        f_rate: rate.f,
                                        g_rate: rate.g,
                                        h_rate: rate.h,
                                        i_rate: rate.i,
                                        j_rate: rate.j,
                                        k_rate: rate.k,
                                        l_rate: rate.l,
                                        m_rate: rate.m,
                                        n_rate: rate.n,
                                        o_rate: rate.o,
                                        p_rate: rate.p,
                                        p1_rate: rate.p1,
                                        p2_rate: rate.p2,
                                        a1_rate: rate.a1,
                                        a2_rate: rate.a2,
                                        r1_rate: rate.r1,
                                        r2_rate: rate.r2,
                                        e2_rate: rate.e2,
                                        created_by: userId,
                                        created_at: new Date().toISOString(),
                                        spreadsheet_type: selectedSpreadsheet,
                                    },
                                    previousData: existingRecords.data[0],
                                });
                                console.log(
                                    `useRatesImport: Successfully updated existing record`
                                );
                            } else {
                                console.error(
                                    'useRatesImport: Could not find existing record to update'
                                );
                                throw createError; // Re-throw original error
                            }
                        } else {
                            // Re-throw non-duplicate errors
                            throw createError;
                        }
                    }
                    processedCount++;
                } catch (error) {
                    console.error(
                        'useRatesImport: Failed to create/update rate record during sequential processing:',
                        rate,
                        'Error:',
                        error
                    );
                    // Re-throw error to be caught by chunk processor and abort the upload
                    throw error;
                }
            }
            console.log(
                `useRatesImport: Successfully processed ${processedCount} records sequentially in this batch.`
            );
        },
        [dataProvider, user?.identity?.id, selectedSpreadsheet]
    );

    return processBatch;
}
